<template>
  <div class="an">
    <div class="hero">
      <div class="wrap hero-grid">
        <div class="intro">
          <span class="eyebrow"><span class="dot"></span>Author Nation LIVE 2026 · Booth A7</span>
          <h1>Get your chapter roasted. <em>Then meet with an editor.</em></h1>
          <p class="lede">
            Paste a chapter and we'll deliver a sharp, funny, honest critique in about a minute. Then sit down with a
            Twig editor for a free 15-minute consult at booth A7, any day Tue Nov 10 to Fri Nov 13.
          </p>
          <div class="trust">
            <span v-for="item in trust" :key="item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M5 12l5 5L20 7" /></svg
              >{{ item }}
            </span>
          </div>
        </div>

        <ol class="steps">
          <li>
            <span class="num">1</span>
            <div>
              <h3>Paste your chapter</h3>
              <p>500 to 5,000 words from your work in progress.</p>
            </div>
          </li>
          <li>
            <span class="num">2</span>
            <div>
              <h3>Read your roast</h3>
              <p>What's working, the one thing that isn't, and what to do about it.</p>
            </div>
          </li>
          <li>
            <span class="num">3</span>
            <div>
              <h3>Pick a time <span class="tag">In person</span></h3>
              <p>Sit down with a real Twig editor for 15 minutes and talk through your chapter. Free.</p>
            </div>
          </li>
        </ol>

        <div id="roast" class="form-col">
          <form class="card form-card" @submit.prevent="submit">
            <h2>Start your roast</h2>
            <p class="sub">Paste a chapter. We'll take it from there.</p>
            <input
              v-model="chapterTitle"
              class="input"
              type="text"
              placeholder="Chapter or manuscript title (optional)"
              maxlength="120"
            />
            <div class="paste">
              <textarea v-model="chapterText" placeholder="Paste your chapter here..." spellcheck="false"></textarea>
              <div class="count-bar">
                <span class="count" :class="countClass">{{ wordCount.toLocaleString() }} words</span>
                <span class="meter"><i :style="meterStyle"></i></span>
                <span>{{ hint }}</span>
              </div>
            </div>
            <button class="btn-primary" type="submit" :disabled="!isValid || submitting">
              {{ submitting ? 'Starting...' : 'Roast my chapter' }}
            </button>
            <p v-if="submitError" class="error">{{ submitError }}</p>
            <p class="legal">
              Twig will never use your writing to train AI. By submitting, you agree to our
              <a href="https://twig.io/terms-of-service" target="_blank">Terms</a> and
              <a href="https://twig.io/privacy-policy" target="_blank">Privacy Policy</a>.
            </p>
            <div class="after-note">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M16 3v4M8 3v4M3 11h18" />
              </svg>
              <span>When your roast is done, you'll get a button to pick your consult time.</span>
            </div>
          </form>
        </div>
      </div>
    </div>

    <section class="what">
      <div class="wrap what-grid">
        <div>
          <div class="section-head flush">
            <div class="kicker">What's a chapter roast?</div>
            <h2>An honest critique of one chapter. With jokes.</h2>
            <p>
              Funnier than a normal edit, and a little meaner (all in good fun). It's specific to your pages, so you
              laugh and then you know exactly what to fix.
            </p>
          </div>
          <ul class="what-list">
            <li>
              <span class="badge b-good">3</span>
              <div>
                <h3>Things that are working</h3>
                <p>Quoted straight from your chapter, so you know what to keep.</p>
              </div>
            </li>
            <li>
              <span class="badge b-roast">1</span>
              <div>
                <h3>Thing getting roasted</h3>
                <p>The biggest problem in the chapter, examined from every angle.</p>
              </div>
            </li>
            <li>
              <span class="badge b-next">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </span>
              <div>
                <h3>What to do now</h3>
                <p>Two or three concrete revision moves you can make tonight.</p>
              </div>
            </li>
          </ul>
        </div>
        <div>
          <div class="card letter">
            <span class="ex">Example</span>
            <p>
              Twelve pages, one storm, and a man named Jason who would like you to know he is thinking. Here are three
              things working, and one thing we need to talk about.
            </p>
            <div class="sec s-good">
              <div class="lbl">What's working</div>
              <strong>The Rain Is Doing More Work Than Jason</strong
              ><span
                >Every weather line earns its place. "The gutters gave up around midnight" is doing the job of a whole
                paragraph.</span
              >
            </div>
            <div class="sec s-good">
              <div class="lbl">What's working</div>
              <strong>Sarah, Briefly, Becomes Interesting</strong
              ><span>Page 9. She lies to her mother and we finally lean in.</span>
            </div>
            <div class="sec s-roast">
              <div class="lbl">The roast</div>
              <strong>"Hastily," "Nervously," "Quietly": A Trilogy</strong
              ><span>Jason does nothing in this chapter without an adverb holding his hand...</span>
            </div>
          </div>
          <div class="fade"></div>
        </div>
      </div>
    </section>

    <section class="reviews">
      <div class="wrap">
        <div class="section-head">
          <div class="kicker">The roast</div>
          <h2>What writers are saying</h2>
        </div>
        <div class="quotes">
          <figure v-for="q in quotes" :key="q.name" class="card quote" :class="{ big: q.big }">
            <div v-if="q.big" class="stars">★★★★★</div>
            <p>"{{ q.quote }}"</p>
            <cite
              ><span class="avatar">{{ q.name[0] }}</span
              >{{ q.name }}</cite
            >
          </figure>
        </div>
        <p class="foot">Real feedback from Twig writers.</p>
      </div>
    </section>

    <section class="no-top">
      <div class="wrap">
        <div class="section-head">
          <div class="kicker">Find us</div>
          <h2>We'll be at booth A7 all week</h2>
          <p>Book your time after your roast, then come find us.</p>
        </div>
        <div class="visit-grid">
          <div class="card hours">
            <div class="hours-head">
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2">
                  <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
                  <circle cx="12" cy="9.5" r="2.5" />
                </svg>
                Consult hours
              </h3>
              <span>Las Vegas time</span>
            </div>
            <dl>
              <div v-for="h in hours" :key="h.day" class="row">
                <dt>{{ h.day }} <span v-if="h.expo" class="expo">Expo day</span></dt>
                <dd>{{ h.time }}</dd>
              </div>
            </dl>
          </div>
          <div class="side-stack">
            <div class="card talk">
              <div class="kicker">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="3" width="6" height="11" rx="3" />
                  <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                </svg>
                Joe's talk
              </div>
              <h3>What's Actually Wrong With Novels</h3>
              <p>The handful of problems that show up in almost every manuscript, and how to spot them in yours.</p>
              <span class="tba">Day and room TBA</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="faq-section">
      <div class="wrap faq-wrap">
        <div class="section-head">
          <div class="kicker">Questions</div>
          <h2>Have a question?</h2>
          <p>Anything else? Find us at booth A7 or email <a href="mailto:support@twig.io">support@twig.io</a>.</p>
        </div>
        <div>
          <details v-for="(f, i) in faqs" :key="f.q" class="faq" :open="i === 0">
            <summary>{{ f.q }}<span class="pm">+</span></summary>
            <p>{{ f.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <div class="final">
      <div class="wrap">
        <div class="final-card">
          <div>
            <h2>Bring us your chapter.</h2>
            <p>Roast now. Talk it through at booth A7.</p>
          </div>
          <a href="#roast" class="btn-primary">Roast my chapter</a>
        </div>
      </div>
    </div>

    <footer class="an-footer">
      <div class="wrap">
        <span>© 2026 Twig</span>
        <span>Author Nation LIVE · Horseshoe Las Vegas · Nov 10 to 13</span>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { track } from '../lib/analytics';
import { CONSULT_VIA } from '../lib/consults';
import { countWords } from '../lib/word-count';
import { useAuthStore } from '../stores/auth';

const MIN_WORDS = 500;
const MAX_WORDS = 5000;
// Shared with ChapterRoastPage, which picks the pending roast up after sign-in and starts it
const LS_KEY = 'pendingChapterRoastProjectId';
const LS_VIA_KEY = 'pendingChapterRoastVia';

const router = useRouter();
const authStore = useAuthStore();

const chapterText = ref('');
const chapterTitle = ref('');
const submitting = ref(false);
const submitError = ref('');

const trust = ['The roast and consult are FREE', 'Takes about a minute', 'Never trains AI'];

const hours = [
  { day: 'Tue Nov 10', time: '10 AM to 4 PM', expo: true },
  { day: 'Wed Nov 11', time: '9 AM to 5 PM' },
  { day: 'Thu Nov 12', time: '9 AM to 5 PM' },
  { day: 'Fri Nov 13', time: '9 AM to 3:30 PM' },
];

const quotes = [
  {
    quote:
      "Not only was it very funny, it had a lot of helpful constructive criticism. It didn't just point at the mistakes, it explained why they weren't working and how to fix them. Loved it!",
    name: 'Andrea',
    big: true,
  },
  {
    quote:
      "I hadn't realized I duplicated a piece of a scene, or that the action was too 'soft.' Didn't realize how much it changed my character's personality. Thanks for that.",
    name: 'Robin Y.',
  },
  { quote: 'You gave me valuable, actionable advice. Thanks!', name: 'Jeanette D.' },
  {
    quote:
      'I love how it hits hard but accurate and yet gives useful feedback to make positive changes. Somehow having a humorous voice makes the constructive criticism easier to take.',
    name: 'Vanessa',
  },
  {
    quote:
      'It gave me where to cut, what to add to make this a better scene. Love the roast. It is fun and constructive without being mean.',
    name: 'Barbara M.',
  },
  {
    quote: "I'm genuinely impressed. It cut straight to the chase and helped me polish my work in minutes.",
    name: 'Fernanda G.',
    big: true,
  },
];

const faqs = [
  {
    q: 'How do I book a consult?',
    a: "Run your roast first. When it's done you'll see a button to pick a time. It takes about a minute.",
  },
  { q: 'Who will I meet?', a: 'One of the Twig editors. You pick the time, we match you with whoever is free.' },
  {
    q: 'Can I change my time?',
    a: 'Yes. Your confirmation email has a link to move or cancel.',
  },
  {
    q: 'Will this train AI on my writing?',
    a: 'No. Twig will never use your manuscript to train AI models. Only you can see your chapter and your roast.',
  },
  { q: 'Is this really free?', a: 'Yes. The roast and the consult are both free.' },
];

const wordCount = computed(() => countWords(chapterText.value));
const isValid = computed(() => wordCount.value >= MIN_WORDS && wordCount.value <= MAX_WORDS);
const countClass = computed(() => (wordCount.value > MAX_WORDS ? 'over' : wordCount.value >= MIN_WORDS ? 'ok' : ''));
const meterStyle = computed(() => ({
  width: `${Math.min(100, (wordCount.value / MIN_WORDS) * 100)}%`,
  background:
    wordCount.value > MAX_WORDS ? 'var(--danger)' : wordCount.value >= MIN_WORDS ? 'var(--success)' : 'var(--accent)',
}));
const hint = computed(() => {
  const n = wordCount.value;
  if (n === 0) return '500 to 5,000 words';
  if (n < MIN_WORDS) return `${(MIN_WORDS - n).toLocaleString()} to go`;
  if (n > MAX_WORDS) return 'Max 5,000';
  return 'Ready';
});

async function submit() {
  if (!isValid.value || submitting.value) return;
  submitError.value = '';
  submitting.value = true;
  try {
    const res = await fetch('/api/chapter-roast/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: chapterText.value, title: chapterTitle.value.trim() || undefined }),
    });
    const data = (await res.json()) as { projectId?: string; error?: string };
    if (!res.ok || !data.projectId) {
      submitError.value = data.error || 'Something went wrong. Please try again.';
      submitting.value = false;
      return;
    }
    localStorage.setItem(LS_KEY, data.projectId);
    localStorage.setItem(LS_VIA_KEY, CONSULT_VIA);
    track('roast_submitted', {
      project_id: data.projectId,
      word_count: wordCount.value,
      signed_in: authStore.isAuthenticated,
      via: CONSULT_VIA,
    });
    // The roast page starts the pending roast and shows progress; signing in first if needed
    if (authStore.isAuthenticated) router.push('/chapter-roast');
    else router.push({ name: 'auth', query: { redirect: '/chapter-roast', context: 'chapter-roast' } });
  } catch {
    submitError.value = 'Network error. Please try again.';
    submitting.value = false;
  }
}

onMounted(() => {
  document.title = 'Author Nation | Twig';
  track('consult_landing_viewed', { via: CONSULT_VIA });
});
</script>

<style scoped>
.an {
  --bg: #ffffff;
  --bg-2: #f8f9fa;
  --bg-3: #f3f4f6;
  --border: #e1e4e8;
  --border-2: #d0d7de;
  --text: #24292f;
  --text-2: #57606a;
  --muted: #8b949e;
  --accent: #61aed4;
  --accent-hover: #4a97b8;
  --accent-deep: #1a5f8a;
  --accent-light: rgba(97, 174, 212, 0.08);
  --accent-tint: #f0f7fc;
  --yellow: #efaf03;
  --success: #1a7f37;
  --danger: #cf222e;
  --radius: 8px;
  --shadow-lg: 0 10px 25px rgba(15, 23, 42, 0.07), 0 2px 6px rgba(15, 23, 42, 0.04);
}
.an {
  font-size: 15px;
  line-height: 1.6;
  color: var(--text);
  background: var(--bg);
}
.an *,
.an *::before,
.an *::after {
  box-sizing: border-box;
}
.an h1,
.an h2,
.an h3 {
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0;
}
.an a {
  color: var(--accent-hover);
}
.wrap {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Hero */
.hero {
  padding: 56px 0 64px;
  background:
    radial-gradient(900px 400px at 85% -10%, rgba(97, 174, 212, 0.1), transparent 60%), linear-gradient(#fff, #fff);
}
.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 440px;
  grid-template-areas: 'intro form' 'steps form';
  grid-template-rows: auto 1fr;
  column-gap: 64px;
  align-items: start;
}
.intro {
  grid-area: intro;
}
.steps {
  grid-area: steps;
}
.form-col {
  grid-area: form;
  position: sticky;
  top: 72px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-3);
  color: var(--text);
  font-weight: 600;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 999px;
}
.eyebrow .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--yellow);
}
.intro h1 {
  font-size: 52px;
  margin: 20px 0 18px;
}
.intro h1 em {
  font-style: normal;
  color: var(--accent-deep);
}
.lede {
  font-size: 19px;
  color: var(--text-2);
  max-width: 34em;
  margin: 0;
}
.trust {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-top: 22px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-2);
}
.trust span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.trust svg {
  color: var(--success);
}

.steps {
  list-style: none;
  padding: 0;
  margin: 36px 0 0;
  align-self: start;
  display: grid;
  gap: 4px;
  position: relative;
}
.steps li {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 16px;
  padding: 12px 0;
  position: relative;
}
.steps li:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 17px;
  top: 50px;
  bottom: -6px;
  width: 2px;
  background: var(--border);
}
.num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent-tint);
  color: var(--accent-deep);
  font-weight: 700;
  display: grid;
  place-items: center;
  font-size: 14px;
  position: relative;
  z-index: 1;
}
.steps li:last-child .num {
  background: var(--accent-deep);
  color: #fff;
}
.steps h3 {
  font-size: 17px;
  margin: 6px 0 2px;
}
.steps p {
  margin: 0;
  color: var(--text-2);
}
.steps .tag {
  display: inline-block;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--accent-deep);
  background: var(--accent-tint);
  padding: 2px 8px;
  border-radius: 4px;
  vertical-align: 2px;
}

/* Form card */
.card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
}
.form-card {
  padding: 28px;
}
.form-card h2 {
  font-size: 22px;
}
.form-card .sub {
  color: var(--text-2);
  margin: 4px 0 20px;
}
.input,
.paste textarea {
  width: 100%;
  font: inherit;
  color: var(--text);
  border: 1px solid var(--border-2);
  border-radius: var(--radius);
  background: #fff;
  padding: 11px 14px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.input:focus,
.paste textarea:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(97, 174, 212, 0.18);
}
.paste {
  margin-top: 12px;
  border: 1px solid var(--border-2);
  border-radius: var(--radius);
  overflow: hidden;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.paste:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(97, 174, 212, 0.18);
}
.paste textarea {
  width: 100%;
  border: 0;
  border-radius: 0;
  height: 260px;
  resize: vertical;
  font-family: 'PT Serif', serif;
  font-size: 15px;
  line-height: 1.7;
  box-shadow: none !important;
}
.count-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  background: var(--bg-2);
  border-top: 1px solid var(--border);
  font-size: 12.5px;
  color: var(--text-2);
}
.meter {
  flex: 1;
  max-width: 140px;
  height: 4px;
  background: var(--border);
  border-radius: 2px;
  overflow: hidden;
}
.meter i {
  display: block;
  height: 100%;
  width: 0;
  background: var(--accent);
  transition:
    width 0.2s,
    background 0.2s;
}
.count.ok {
  color: var(--success);
  font-weight: 600;
}
.count.over {
  color: var(--danger);
  font-weight: 600;
}
.btn-primary {
  width: 100%;
  margin-top: 16px;
  padding: 15px;
  border: 0;
  border-radius: var(--radius);
  background: var(--accent);
  color: #fff;
  font: inherit;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.2s,
    transform 0.2s,
    box-shadow 0.2s;
}
.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}
.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.legal {
  font-size: 12px;
  color: var(--muted);
  text-align: center;
  margin: 12px 0 0;
}
.legal a {
  color: var(--text-2);
}
.after-note {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-top: 20px;
  padding: 14px 16px;
  background: var(--accent-tint);
  border-radius: var(--radius);
  font-size: 13.5px;
  color: var(--text);
}
.after-note svg {
  flex: none;
  color: var(--accent-deep);
  margin-top: 2px;
}

/* Section basics */
.an section {
  padding: 72px 0;
}
.section-head {
  max-width: 640px;
  margin-bottom: 36px;
}
.kicker {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-deep);
  margin-bottom: 10px;
}
.section-head h2 {
  font-size: 32px;
}
.section-head p {
  color: var(--text-2);
  font-size: 17px;
  margin: 10px 0 0;
}

/* Consult length */
.tenmin {
  background: var(--bg-2);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}
.three {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
.tile {
  padding: 24px;
}
.tile .mins {
  font-family: 'PT Serif', serif;
  font-size: 28px;
  font-weight: 700;
  color: var(--accent-deep);
  line-height: 1;
}
.tile .mins small {
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--muted);
  margin-left: 4px;
}
.tile h3 {
  font-size: 17px;
  margin: 14px 0 6px;
}
.tile p {
  margin: 0;
  color: var(--text-2);
}

/* Visit */
.visit-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 20px;
}
.hours {
  padding: 24px 28px;
}
.hours-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.hours-head h3 {
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.hours-head span {
  color: var(--muted);
  font-size: 13px;
}
.hours dl {
  margin: 0;
}
.hours .row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
}
.hours .row:last-child {
  border-bottom: 0;
}
.hours dt {
  font-weight: 500;
}
.hours dd {
  margin: 0;
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}
.hours .row .expo {
  font-size: 11px;
  font-weight: 600;
  color: var(--accent-deep);
  background: var(--accent-tint);
  padding: 2px 7px;
  border-radius: 4px;
  margin-left: 8px;
}
.side-stack {
  display: grid;
  gap: 20px;
}
.talk {
  padding: 24px 28px;
  background: #fff;
}
.talk .kicker {
  display: flex;
  align-items: center;
  gap: 8px;
}
.talk h3 {
  font-family: 'PT Serif', serif;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0;
  margin: 4px 0 6px;
}
.talk p {
  margin: 0;
  color: var(--text-2);
}
.tba {
  display: inline-block;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-2);
  background: var(--bg-3);
  padding: 4px 10px;
  border-radius: 999px;
}
.remote {
  padding: 22px 28px;
  border-style: dashed;
  box-shadow: none;
}
.remote h3 {
  font-size: 16px;
  margin-bottom: 4px;
}
.remote p {
  margin: 0;
  color: var(--text-2);
}

/* What's a roast */
.what {
  padding: 72px 0;
  border-top: 1px solid var(--border);
}
.what-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: 56px;
  align-items: center;
}
.what-list {
  list-style: none;
  padding: 0;
  margin: 28px 0 0;
  display: grid;
  gap: 18px;
}
.what-list li {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 14px;
  align-items: start;
}
.badge {
  height: 44px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 15px;
}
.b-good {
  background: #f2faf5;
  color: #2d9e5f;
}
.b-roast {
  background: var(--accent-tint);
  color: var(--accent-deep);
}
.b-next {
  background: var(--bg-3);
  color: var(--text-2);
}
.what-list h3 {
  font-size: 16px;
  margin: 2px 0 2px;
}
.what-list p {
  margin: 0;
  color: var(--text-2);
}
.letter {
  padding: 52px 40px 36px;
  font-family: 'PT Serif', serif;
  font-size: 15px;
  line-height: 1.7;
  position: relative;
}
.letter .ex {
  position: absolute;
  top: 16px;
  right: 16px;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  background: var(--bg-3);
  padding: 3px 8px;
  border-radius: 4px;
}
.letter p {
  margin: 0 0 14px;
}
.sec {
  border-left: 3px solid;
  border-radius: 4px;
  padding: 12px 16px;
  margin: 0 0 12px;
}
.sec .lbl {
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.sec strong {
  display: block;
  font-size: 16px;
  margin-bottom: 2px;
}
.sec span {
  color: var(--text-2);
  font-size: 14px;
}
.s-good {
  border-color: #2d9e5f;
  background: #f2faf5;
}
.s-good .lbl {
  color: #2d9e5f;
}
.s-roast {
  border-color: #1a5f8a;
  background: #f0f7fc;
}
.s-roast .lbl {
  color: #1a5f8a;
}
.fade {
  height: 40px;
  margin-top: -40px;
  position: relative;
  background: linear-gradient(rgba(255, 255, 255, 0), #fff);
}

/* Testimonials */
.reviews {
  overflow: hidden;
  background: var(--bg-2);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  margin-bottom: 72px;
}
.quotes {
  columns: 3;
  column-gap: 20px;
}
.quote {
  break-inside: avoid;
  margin: 0 0 20px;
  padding: 22px 24px;
}
.quote p {
  font-family: 'PT Serif', serif;
  font-size: 16px;
  line-height: 1.65;
  margin: 0 0 14px;
}
.quote.big p {
  font-size: 19px;
}
.quote cite {
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-deep);
  background: var(--accent-tint);
}
.stars {
  color: var(--yellow);
  letter-spacing: 2px;
  font-size: 13px;
  margin-bottom: 10px;
}
.foot {
  color: var(--muted);
  font-size: 13px;
  margin-top: 4px;
}

/* FAQ */
.faq-wrap {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 64px;
}
details.faq {
  border-bottom: 1px solid var(--border);
}
details.faq:first-child {
  border-top: 1px solid var(--border);
}
details.faq summary {
  list-style: none;
  cursor: pointer;
  padding: 18px 0;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}
details.faq summary::-webkit-details-marker {
  display: none;
}
details.faq summary .pm {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--bg-3);
  display: grid;
  place-items: center;
  color: var(--text-2);
  transition: transform 0.2s;
}
details.faq[open] summary .pm {
  transform: rotate(45deg);
  background: var(--accent-tint);
  color: var(--accent-deep);
}
details.faq p {
  margin: 0 0 18px;
  color: var(--text-2);
  max-width: 40em;
}

/* Final CTA */
.final {
  padding: 0 0 80px;
}
.final-card {
  background: var(--text);
  color: #fff;
  border-radius: 16px;
  padding: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}
.final-card h2 {
  font-size: 30px;
}
.final-card p {
  color: #c9d1d9;
  margin: 8px 0 0;
  font-size: 16px;
}
.final-card .btn-primary {
  width: auto;
  margin: 0;
  padding: 15px 28px;
  white-space: nowrap;
  text-decoration: none;
  display: inline-block;
}

.an-footer {
  border-top: 1px solid var(--border);
  padding: 28px 0;
  color: var(--muted);
  font-size: 13px;
}
.an-footer .wrap {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

/* Mobile */
@media (max-width: 900px) {
  .hero-grid {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'intro' 'form' 'steps';
    grid-template-rows: auto;
  }
  .form-col {
    position: static;
    margin-top: 28px;
  }
  .intro h1 {
    font-size: 36px;
    margin-top: 16px;
  }
  .lede {
    font-size: 17px;
  }
  .three,
  .visit-grid,
  .faq-wrap,
  .what-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .what-grid {
    gap: 32px;
  }
  .letter {
    padding: 48px 22px 28px;
  }
  .faq-wrap {
    gap: 8px;
  }
  .quotes {
    columns: 1;
  }
  .final-card {
    flex-direction: column;
    align-items: flex-start;
    padding: 32px 24px;
  }
}
@media (max-width: 520px) {
  .wrap {
    padding: 0 16px;
  }
  .hero {
    padding: 32px 0 40px;
  }
  .form-card {
    padding: 20px;
  }
  .paste textarea {
    height: 200px;
  }
  .an section {
    padding: 56px 0;
  }
  .section-head h2 {
    font-size: 26px;
  }
}

.flush {
  margin-bottom: 0;
}
.no-top {
  padding-top: 0 !important;
}
.faq-section {
  padding-top: 24px !important;
}
.error {
  color: var(--danger);
  font-size: 13px;
  margin: 10px 0 0;
  text-align: center;
}
.wrap {
  width: 100%;
}
</style>
